import { date } from 'quasar'
import * as emailValidator from 'email-validator'
import { BookDto, HasIndex, newDate, roundWithPrecision, validInt, bsConstants } from 'booksprout'
import { DATE_FORMAT } from '../constants'
import { ellipsis as ellipsisText } from '../utils'

const { formatDate: quasarFormatDate } = date

/**
 * The display helpers out of `CommonBaseMixin` — dates, names, truncation, subscription discounts.
 *
 * Two of them need a translation for their fallback text, so the composable takes one. It is a
 * required argument rather than an optional one with a key-echo fallback: a missing translator would
 * otherwise put `system.labels.anonymous` on the page, which is worse than a compile error.
 *
 * `CommonBaseMixin` now delegates to this. In stage 8 a component reaches for it directly.
 */

export type Translate = (key: string, ...args: any[]) => string

export function useFormatting (tc: Translate) {
  const ellipsis = (text: string, length: number, ellipsisStr = '...', cb: any = void 0) => {
    return ellipsisText(text, length, ellipsisStr, cb)
  }

  /**
   * A ten-digit number is a unix timestamp in seconds, which `new Date()` reads as 1970 — the `* 1000`
   * is what stops `Date.now() / 1000` values rendering as the epoch.
   */
  const formatDate = (timestamp: number | Date, format: string = DATE_FORMAT): string => {
    if (!timestamp) {
      return tc('system.labels.unknownDate')
    }

    if (typeof timestamp === 'number' && (validInt(timestamp) && timestamp.toString().length === 10)) {
      timestamp = timestamp * 1000
    }

    return quasarFormatDate(new Date(timestamp).toUTCString(), format)
  }

  /**
   * "Book 3 of Star Wars Thrawn trilogy" — one string, because the markup could no longer be trusted
   * to supply the spaces between its parts.
   *
   * Four templates built this line out of three conditional inline elements on separate lines and let
   * the whitespace between them do the spacing. Under Vue 2 (`preserveWhitespace: false`) the
   * whitespace-only nodes BETWEEN the elements went, but the indentation INSIDE each one survived, so
   * the text still read as words. Vue 3's compiler condenses, which also trims the leading and
   * trailing whitespace inside each element — and the line rendered "Book 3ofStar Wars Thrawn
   * trilogy". Nothing but a browser shows that: the templates are valid and every word is present.
   */
  const bookSeriesLabel = (book: Partial<BookDto> | undefined): string => {
    if (!book) {
      return ''
    }

    const parts: string[] = []

    if (book.seriesNumber) {
      parts.push(`${tc('system.labels.book')} ${book.seriesNumber}`)
    }

    if (book.seriesNumber && book.seriesName) {
      parts.push(tc('system.labels.of'))
    }

    if (book.seriesName) {
      parts.push(book.seriesName)
    }

    return parts.join(' ')
  }

  /**
   * A calendar day (`YYYY-MM-DD`) rather than an instant — an Apple Books code expiry, which is a
   * DATE column precisely so that no timezone is involved.
   *
   * Parsed by hand into a LOCAL date rather than handed to `new Date`, which reads a bare date as
   * UTC midnight and so renders the day before for anyone behind UTC. Going through `formatDate`
   * would reintroduce exactly the shift the DATE column exists to remove. [sc-28048]
   */
  const formatCalendarDay = (day: string | undefined, format: string = DATE_FORMAT): string => {
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day ?? '')

    if (parts === null) {
      return tc('system.labels.unknownDate')
    }

    return quasarFormatDate(
      new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3])),
      format
    )
  }

  const getDisplayName = (name: string | undefined): string => {
    if (name && name.trim()) {
      return name
    }

    return tc('system.labels.anonymous')
  }

  const getPenNameName = (book: BookDto, field: string) => {
    const penName = book.authorPenName ? (book.authorPenName as HasIndex)[field] : book.penName
    return ellipsis(penName, 50)
  }

  /**
   * Audio review sites carry '(Audio)' in the description because the site list is shared with ebooks;
   * the reviewer does not need telling twice.
   */
  const userFriendlySiteDescription = (description: string): string => {
    if (description.endsWith('(Audio)')) {
      return description.replace('(Audio)', '').trim()
    }

    return description
  }

  const validEmail = (email: string): boolean => {
    return emailValidator.validate(email)
  }

  const minusDiscountNoDate = (amount: number, pcDiscount: number) => {
    return roundWithPrecision(amount * ((100 - pcDiscount) / 100), 2)
  }

  const minusDiscount = (amount: number, pcDiscount: number, untilDate?: Date) => {
    if (untilDate && newDate() <= untilDate) {
      return minusDiscountNoDate(amount, pcDiscount)
    }

    return amount
  }

  const minusSubDiscount = (amount: number, type = 'month') => {
    return minusDiscount(
      amount,
      type === 'month'
        ? bsConstants.SUBSCRIPTIONS.DISCOUNTS.MONTH
        : bsConstants.SUBSCRIPTIONS.DISCOUNTS.YEAR,
      bsConstants.SUBSCRIPTIONS.DISCOUNTS.END_DATE
    )
  }

  const minusSubDiscountNoDate = (amount: number, type = 'month') => {
    return minusDiscountNoDate(
      amount,
      type === 'month'
        ? bsConstants.SUBSCRIPTIONS.DISCOUNTS.MONTH
        : bsConstants.SUBSCRIPTIONS.DISCOUNTS.YEAR
    )
  }

  return {
    ellipsis,
    formatDate,
    bookSeriesLabel,
    formatCalendarDay,
    getDisplayName,
    getPenNameName,
    userFriendlySiteDescription,
    validEmail,
    minusDiscount,
    minusDiscountNoDate,
    minusSubDiscount,
    minusSubDiscountNoDate
  }
}
